import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Pen, Trash2 } from 'lucide-react';
import merge from 'lodash.merge';

import { getByPath } from '@/utils';
import { CommentViewImpl } from '../comment/comment.view';
import { CommentForm } from './commentForm/commentForm';
import { PATHS } from '../comment/constants.comment';
import { CommentView } from '@/components/ui/widget/views/comment/comment.view.type';
import { User } from '@/types/account.types';
import { Button } from '@/shadcn/button';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent
} from '@/shadcn/tooltip';
import { type CommentFeedView } from './commentFeed.view.type';
import { type WidgetData } from '@/components/ui/widget/widget.type';

export type ICommentData = {
  date: string;
  user: {
    name: User['name'];
    avatar?: User['avatar'];
    accountId: User['rowId'];
  };
  group?: string;
  action?: string;
  comment: any;
  groupId?: string;
  groupLabel?: string;
};

type Props = {
  view: CommentFeedView;
  data: WidgetData;
  update: any;
};

const viewDef = {
  namePath: PATHS.NAME,
  avatarPath: PATHS.AVATAR,
  groupPath: PATHS.GROUP,
  datePath: PATHS.DATE,
  actionPath: PATHS.ACTION,
  commentPath: PATHS.COMMENT
} satisfies Partial<CommentView> as CommentView;

const isSameArray = (a: unknown, b: unknown): boolean => {
  return JSON.stringify(a) === JSON.stringify(b);
};

export const CommentFeedViewImpl = memo(({ view, data, update }: Props) => {
  const { path, editor } = view;
  const { patch, onChange, setIsPatched } = update || {};

  const initialComments = getByPath<ICommentData[]>(data, path);

  const dataWithPatchApplied = useMemo(
    () => merge({}, data, patch),
    [data, patch]
  );
  const patchedComments =
    getByPath<ICommentData[]>(dataWithPatchApplied, path) || [];

  useEffect(() => {
    if (setIsPatched)
      setIsPatched(!isSameArray(patchedComments, initialComments));
  }, [initialComments, patchedComments]);

  const [updatedComment, setUpdatedComment] =
    useState<ICommentData[]>(patchedComments);

  const [editingComments, setEditingComments] = useState<number[]>([]);

  const getIsNewComment = (index: number): boolean => {
    if (!initialComments?.length) {
      return true;
    }
    return index > initialComments?.length - 1;
  };

  const handleChange = useCallback(
    value => {
      const isSameAsData =
        isSameArray(value, initialComments) ||
        (initialComments === undefined && value.length === 0);
      if (onChange) {
        onChange({ path, value: isSameAsData ? undefined : value });
      }
    },
    [path, onChange]
  );

  useEffect(() => {
    handleChange(updatedComment);
  }, [updatedComment]);

  const handleEdit = (index: number) => {
    setEditingComments(prevEditingComments => [
      ...new Set([...prevEditingComments, index])
    ]);
  };
  const handleCancelEdit = (index: number) => {
    setEditingComments(prevEditingComments =>
      prevEditingComments.filter(i => i !== index)
    );
  };
  const handleRemove = (index: number) => {
    setUpdatedComment(prevComments =>
      prevComments.filter((_, i) => i !== index)
    );
  };

  const saveComment = (comment: any, index?: number) => {
    setUpdatedComment(prevComments => {
      const newComments = prevComments ? [...prevComments] : [];
      if (index !== undefined) {
        newComments[index] = comment;
      } else {
        newComments.push(comment);
      }
      return newComments;
    });
    if (index !== undefined) {
      handleCancelEdit(index);
    }
  };

  return (
    <div className="flex h-full max-h-[70vh] flex-col gap-4 p-1">
      <div className="scrollbar-thin flex h-full flex-col gap-5 overflow-y-auto">
        {patchedComments.length === 0 && (
          <div className="text-muted-foreground flex items-center justify-center py-5">
            No comments yet
          </div>
        )}
        {patchedComments.map((patchedComment, index) => {
          const isNewComment = getIsNewComment(index);
          return (
            <div
              key={index}
              className="relative flex items-start justify-between pl-2"
              data-cy="commentFeed-item"
            >
              {editingComments.includes(index) ? (
                <div className="flex w-full flex-col pr-2">
                  <CommentForm
                    id={index}
                    data={patchedComment}
                    onSave={saveComment}
                    onCancel={handleCancelEdit}
                  />
                </div>
              ) : (
                <>
                  {isNewComment && (
                    <div className="bg-secondary absolute top-0 left-0 h-full w-1 rounded-full" />
                  )}
                  <div className="relative grow">
                    <CommentViewImpl
                      // key={JSON.stringify(patchedComment)} WTF?
                      data={patchedComment}
                      view={viewDef}
                    />
                    {isNewComment && onChange && (
                      <div className="absolute top-0 right-0 flex">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEdit(index)}
                                data-cy="edit-comment"
                              >
                                <Pen />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit comment</TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghostDestructiveSubtle"
                                size="icon"
                                onClick={() => handleRemove(index)}
                                data-cy="delete-comment"
                              >
                                <Trash2 />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete comment</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      {editor?.type === 'addComment' && onChange && (
        <div>
          <CommentForm onSave={saveComment} onCancel={handleCancelEdit} />
        </div>
      )}
    </div>
  );
});

// get edited path
// returns path if editor is set, null otherwise
const getEditedPath = view => {
  const { path, editor } = view;
  return editor ? path : null;
};

export const commentFeedViewProvider = {
  component: CommentFeedViewImpl,
  getEditedPath
};
